import React from 'react';

import {
  formatProperties,
  formatPurchases,
  formatCompactCurrency,
  formatOwnership,
  formatCount,
} from 'data/investors';
import { formatCurrency, formatCountWithShare } from './format';
import css from './style.scss';

// Portfolio & performance metric grid — Figma DataLine, line=3 (278:20073),
// inside the Investor Profile card. Three rows of four metrics; row 1 mirrors
// the search card fields (../Results/InvestorCard). All null-safe.
//
// `investor.metrics` is now the ATAI-109 `InvestorProfileMetrics` shape
// (GET /ps-core/investors/{id}, merged in by Profile/index.js's
// useInvestorProfileData once it resolves) — every field below has a real,
// confirmed source: totalEquity/totalEstimatedValue (row 2, full currency —
// this grid never abbreviates, unlike the search card's "$248K"),
// cashPurchaseCount/financedCount (row 2), and
// propertyTypeDistribution.residentialSingleFamilyCount/ownerOccupiedCount/
// nonOwnerOccupiedCount (row 3). Every field is nullable per the DTO — the
// formatters below already render null/undefined as "—", so a sparse
// snapshot just shows dashes rather than needing special-casing.
//
// Until the fetch resolves, `investor` is still just the search-result
// object (no `metrics` beyond the card subset) — the same formatters handle
// that interim state as a sparse payload, same as a sparse real one.
const Metric = ({ label, value }) => (
  <div className={css.gridMetric}>
    <span className={css.gridMetric__label}>{label}</span>
    <span className={css.gridMetric__value}>{value}</span>
  </div>
);

const Row = ({ children }) => <div className={css.gridRow}>{children}</div>;

const MetricGrid = ({ investor }) => {
  const metrics = investor.metrics || {};
  const distribution = metrics.propertyTypeDistribution || {};
  const purchaseTotal =
    metrics.cashPurchaseCount == null && metrics.financedCount == null
      ? undefined
      : (metrics.cashPurchaseCount || 0) + (metrics.financedCount || 0);

  return (
    <div className={css.grid}>
      <Row>
        <Metric label="Portfolio Size" value={formatProperties(metrics.portfolioSize)} />
        <Metric
          label="Last 12 Months"
          value={formatPurchases(metrics.acquisitionCountLast12Months)}
        />
        <Metric
          label="Avg Purchase Price"
          value={formatCompactCurrency(metrics.avgPurchasePrice)}
        />
        <Metric label="Ownership" value={formatOwnership(metrics.avgOwnershipLengthMonths)} />
      </Row>
      <Row>
        <Metric label="Total Equity" value={formatCurrency(metrics.totalEquity)} />
        <Metric label="Est. Portfolio Value" value={formatCurrency(metrics.totalEstimatedValue)} />
        <Metric
          label="Cash Purchases"
          value={formatCountWithShare(metrics.cashPurchaseCount, purchaseTotal)}
        />
        <Metric label="Financed" value={formatCount(metrics.financedCount)} />
      </Row>
      <Row>
        <Metric
          label="Residential (Single Family)"
          value={formatCount(distribution.residentialSingleFamilyCount)}
        />
        <Metric
          label="Dispositions (All Time)"
          value={formatCount(metrics.totalDispositionCount)}
        />
        <Metric label="Owner Occupied" value={formatCount(metrics.ownerOccupiedCount)} />
        <Metric label="Non Owner Occupied" value={formatCount(metrics.nonOwnerOccupiedCount)} />
      </Row>
    </div>
  );
};

export default MetricGrid;
